import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollReveal from '@/components/base/ScrollReveal';
import { articles, categories } from '@/mocks/articles';

const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);
const latestArticles = articles.filter((a) => !a.featured).slice(0, 6);
const worksArticles = articles.filter((a) => a.category === 'works').slice(0, 3);

const faqItems = [
  {
    question: '田中瞳的最新作品在哪里可以观看？',
    answer: '田中瞳的最新作品《星光之约》预计于2026年10月在东京电视台首播。您也可以关注本站「作品集」栏目，我们将第一时间更新田中瞳所有作品的观看渠道和播出信息。',
  },
  {
    question: '如何获取田中瞳的最新动态？',
    answer: '您可以通过本站「最新动态」栏目追踪田中瞳的实时资讯。此外，田中瞳在Instagram、Twitter等社交媒体平台也设有官方账号，欢迎关注获取第一手消息。',
  },
  {
    question: '田中瞳有官方粉丝俱乐部吗？',
    answer: '田中瞳设有官方粉丝俱乐部，会员可享受独家内容、优先购票、限量周边等专属福利。详情请关注本站「粉丝社区」栏目的最新公告。',
  },
  {
    question: '田中瞳的写真集在哪里可以购买？',
    answer: '田中瞳的最新写真集《花语》已在日本各大书店及在线商城发售。您也可以关注本站「作品集」栏目，获取最新出版物的购买链接和发售信息。',
  },
  {
    question: '如何联系田中瞳进行商业合作？',
    answer: '商业合作请通过田中瞳所属经纪公司的官方渠道进行联系。本站仅提供资讯服务，不代理任何商业合作事务。您可以在「关于我们」页面了解更多信息。',
  },
];

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '田中瞳官方资讯站 - 首页',
  description: '田中瞳最新动态、作品资讯、个人简介一站汇聚',
  url: 'https://lampbus.cn',
  isPartOf: {
    '@type': 'WebSite',
    name: '田中瞳官方资讯站',
    url: 'https://lampbus.cn',
  },
  about: {
    '@type': 'Person',
    name: '田中瞳',
    description: '日本知名演员，出演多部热门电视剧和电影作品',
  },
};

export default function Home() {
  return (
    <>
      <SEOHead
        title="首页"
        description="田中瞳最新动态、作品资讯、个人简介一站汇聚。深度了解田中瞳的演艺经历、代表作品与粉丝社区，第一时间掌握田中瞳相关最新消息。"
        keywords="田中瞳,田中瞳作品,田中瞳最新动态,田中瞳个人简介,田中瞳演艺经历,田中瞳粉丝"
        canonicalUrl="https://lampbus.cn"
        schemaJson={homeSchema}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20soft%20artistic%20abstract%20composition%20with%20warm%20cream%20and%20rose%20gold%20gradients%2C%20elegant%20flowing%20fabric%20textures%2C%20minimal%20Japanese%20aesthetic%2C%20sophisticated%20editorial%20style%2C%20soft%20diffused%20lighting%2C%20dreamy%20atmosphere%2C%20no%20text%2C%20no%20people&width=1600&height=800&seq=tanaka-hero-01&orientation=landscape"
          alt="田中瞳官方资讯站"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full max-w-[800px] mx-auto px-4 md:px-6 text-center animate-fade-in">
          <p className="text-sm md:text-base text-white/80 tracking-[0.3em] uppercase mb-4 font-label">
            Hitomi Tanaka Official News
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight">
            田中瞳
            <span className="block text-xl md:text-2xl lg:text-3xl font-normal mt-2 text-white/90">
              官方资讯与作品全览
            </span>
          </h1>
          <p className="text-sm md:text-base text-white/70 max-w-[600px] mx-auto mb-8 leading-relaxed">
            最新动态、作品资讯、个人简介一站汇聚。深度了解田中瞳的演艺经历、代表作品与粉丝社区，第一时间掌握相关最新消息。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/latest-news"
              className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览最新动态
              <i className="ri-arrow-right-line ml-1.5"></i>
            </Link>
            <Link
              to="/category/works"
              className="px-6 py-3 bg-white/15 backdrop-blur-sm text-white rounded-full text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer border border-white/20"
            >
              查看作品集
            </Link>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                内容导航
              </h2>
              <p className="text-sm text-foreground-600 max-w-[500px] mx-auto">
                探索田中瞳的精彩世界，从最新动态到经典作品，一站式浏览
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat, index) => (
              <ScrollReveal key={cat.id} delay={index * 80} animation="slide-up">
                <Link
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-4 md:p-5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors group cursor-pointer"
                >
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                    <i className={`text-lg md:text-xl ${getCategoryIcon(cat.slug)}`}></i>
                  </span>
                  <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors text-center whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  最新动态
                </h2>
                <p className="text-sm text-foreground-600">
                  田中瞳的最新新闻与资讯
                </p>
              </div>
              <Link
                to="/category/latest-news"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </ScrollReveal>

          {featuredArticles.length > 0 && (
            <ScrollReveal animation="slide-up">
              <div className="mb-10">
                <ArticleCard {...featuredArticles[0]} featured />
              </div>
            </ScrollReveal>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {latestArticles.slice(0, 3).map((article, index) => (
              <ScrollReveal key={article.id} delay={index * 150} animation="slide-up">
                <ArticleCard {...article} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Works Gallery */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  代表作品
                </h2>
                <p className="text-sm text-foreground-600">
                  田中瞳的经典影视作品
                </p>
              </div>
              <Link
                to="/category/works"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看全部作品
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {worksArticles.map((article, index) => (
              <ScrollReveal key={article.id} delay={index * 150} animation="slide-up">
                <ArticleCard {...article} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* About Intro */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <ScrollReveal animation="slide-in-left" className="w-full lg:w-1/2">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20elegant%20abstract%20composition%20with%20flowing%20silk%20fabric%20textures%20in%20cream%20and%20rose%20tones%2C%20minimal%20Japanese%20aesthetic%2C%20soft%20diffused%20lighting%2C%20artistic%20editorial%20style%2C%20sophisticated%20and%20dreamy%20atmosphere%2C%20no%20text%2C%20no%20people&width=800&height=600&seq=tanaka-about-intro&orientation=landscape"
                  alt="关于田中瞳"
                  className="w-full h-full object-cover"
                />
              </div>
            </ScrollReveal>
            <ScrollReveal animation="slide-in-right" className="w-full lg:w-1/2">
              <p className="text-xs text-primary-600 font-medium tracking-wider uppercase mb-3">
                About Hitomi Tanaka
              </p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                关于田中瞳
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                田中瞳，日本知名演员，自2016年出道以来，凭借精湛的演技与独特的个人魅力，在影视界稳步崛起。从《青春和弦》的青涩出道，到《午夜旋律》的巅峰演绎，她用十年时间书写了一段精彩的演艺篇章。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                多次获得行业重要奖项认可，田中瞳不仅是实力派演员的代表，更积极投身公益事业，于2026年出任联合国儿童基金会亲善大使。她的每一步成长，都凝聚着对表演艺术的热爱与执着。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                了解更多
                <i className="ri-arrow-right-line"></i>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-18 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <ScrollReveal>
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于田中瞳的常见疑问解答
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <ScrollReveal key={index} delay={index * 100} animation="slide-up">
                <details className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-800 marker:hidden">
                    <span>{item.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20abstract%20gradient%20with%20cream%20rose%20gold%20and%20soft%20amber%20tones%2C%20elegant%20flowing%20textures%2C%20minimal%20Japanese%20aesthetic%2C%20dreamy%20and%20sophisticated%2C%20no%20people%2C%20no%20text&width=1600&height=600&seq=tanaka-cta-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45"></div>
        <ScrollReveal className="relative z-10 max-w-[600px] mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
            不错过田中瞳的每一个精彩瞬间
          </h2>
          <p className="text-sm text-white/70 mb-7 leading-relaxed">
            订阅我们的资讯推送，第一时间获取田中瞳最新动态、作品发布和粉丝活动信息
          </p>
          <Link
            to="/category/latest-news"
            className="inline-flex items-center gap-1.5 px-7 py-3 bg-accent-500 text-white rounded-full text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            立即探索
            <i className="ri-arrow-right-line"></i>
          </Link>
        </ScrollReveal>
      </section>
    </>
  );
}

function getCategoryIcon(slug: string): string {
  const icons: Record<string, string> = {
    'latest-news': 'ri-flashlight-line',
    works: 'ri-movie-2-line',
    interviews: 'ri-chat-quote-line',
    career: 'ri-road-map-line',
    gallery: 'ri-image-line',
    community: 'ri-heart-line',
  };
  return icons[slug] || 'ri-folder-line';
}